/**
 * Pricing rules for the activity Pricing tab: age groups and group size tiers
 * ride inside `resource` on the product endpoints, each row a signed euro
 * `amount` on top of the chosen duration's price (-5 is a EUR 5 discount).
 */

interface IAgeGroupRow {
  id?: string;
  label?: string;
  amount?: number | string | null;
}

interface IGroupSizeRow {
  id?: string;
  label?: string;
  minParticipant?: number | string | null;
  maxParticipant?: number | string | null;
  amount?: number | string | null;
}

interface IDurationRow {
  value?: number | string | null;
  price?: number | string | null;
}

/** Money and counts arrive as numbers, decimal strings or blank inputs. */
const toNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const groupSizeLabel = (min: unknown, max: unknown): string => {
  const lo = toNumber(min);
  const hi = toNumber(max);
  if (lo === undefined) return '';
  if (hi === undefined) return `${lo}+ participants`;
  if (hi === lo) return `${lo} participant${lo === 1 ? '' : 's'}`;
  return `${lo}–${hi} participants`;
};

/** The tier a headcount falls in; a blank maximum means "and up". */
const findGroupSizeTier = <T extends IGroupSizeRow>(
  tiers: T[] | undefined,
  headcount: number
): T | undefined =>
  (tiers ?? []).find(tier => {
    const lo = toNumber(tier.minParticipant);
    const hi = toNumber(tier.maxParticipant);
    if (lo === undefined) return false;
    return headcount >= lo && (hi === undefined || headcount <= hi);
  });

/**
 * Tiers must cover every headcount the activity accepts: start at 1, run on
 * without gaps or overlaps, and only the last may be open-ended. A closed last
 * tier is fine when it reaches the activity's max participants. The backend
 * does not check this, so the form does.
 */
const groupSizeCoverageError = (
  tiers: IGroupSizeRow[] | undefined,
  maxParticipant?: unknown
): string | undefined => {
  if (!tiers || tiers.length === 0) return undefined;

  let expected = 1;
  for (let i = 0; i < tiers.length; i += 1) {
    const lo = toNumber(tiers[i].minParticipant);
    const hi = toNumber(tiers[i].maxParticipant);
    const isLast = i === tiers.length - 1;

    if (lo === undefined) return undefined; // the row's own error shows
    if (lo !== expected) {
      return i === 0
        ? 'The first group size must start at 1 participant.'
        : `Group sizes must run on without gaps or overlaps: the tier after ${expected - 1} should start at ${expected}.`;
    }
    if (hi === undefined) {
      if (!isLast) return 'Only the last group size can be open-ended.';
      return undefined;
    }
    if (hi < lo) return undefined; // the row's own error shows
    expected = hi + 1;
  }

  const max = toNumber(maxParticipant);
  if (max !== undefined && expected - 1 < max) {
    return `Group sizes stop at ${expected - 1}, but this activity takes up to ${max}. Extend the last tier or leave its maximum empty.`;
  }
  return undefined;
};

interface IPreviewLine {
  label: string;
  count: number;
  unitPrice: number;
  total: number;
}

interface IPricingPreviewInput {
  /** The chosen duration's list price, before any amounts or smart pricing. */
  durationPrice: number;
  perPerson: boolean;
  taxPercentage?: number;
  ageGroups?: IAgeGroupRow[];
  groupSizes?: IGroupSizeRow[];
  /** Headcount per age group row, or one entry when there are no age groups. */
  counts: number[];
  /** The timeslot's smart pricing rule, run on each finished unit price. */
  applySmartPricing?: (price: number) => number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * What the customer pays: the duration price plus the age group's amount plus
 * the headcount tier's amount, per person, floored at 0, and only then the
 * timeslot's smart pricing (the client's order: smart pricing comes after the
 * age group and group size calculation). Priced per unit, it is charged once:
 * the duration price, the amount of the first age group (in the activity's
 * order) that has people, and the tier amount. Entered amounts include tax, so VAT is
 * taken out of the total rather than added.
 */
const calculatePricingPreview = ({
  durationPrice,
  perPerson,
  taxPercentage,
  ageGroups,
  groupSizes,
  counts,
  applySmartPricing,
}: IPricingPreviewInput) => {
  const hasAgeGroups = (ageGroups?.length ?? 0) > 0;
  const headcount = counts.reduce((sum, n) => sum + Math.max(0, n), 0);
  const tier = findGroupSizeTier(groupSizes, headcount);
  const tierAmount = toNumber(tier?.amount) ?? 0;

  let totalBeforeSmartPricing = 0;
  const unitPriceFor = (amount: number, count: number) => {
    const beforeSmart = Math.max(0, durationPrice + amount + tierAmount);
    totalBeforeSmartPricing += round2(beforeSmart) * count;
    const smart = applySmartPricing
      ? applySmartPricing(beforeSmart)
      : beforeSmart;
    return round2(Math.max(0, smart));
  };

  let lines: IPreviewLine[];
  if (!perPerson) {
    const first = hasAgeGroups
      ? ageGroups?.find((_, index) => (counts[index] ?? 0) > 0)
      : undefined;
    const unitPrice = unitPriceFor(toNumber(first?.amount) ?? 0, 1);
    lines = [{ label: 'booking', count: 1, unitPrice, total: unitPrice }];
  } else if (hasAgeGroups) {
    lines = (ageGroups ?? []).map((group, index) => {
      const count = Math.max(0, counts[index] ?? 0);
      const unitPrice = unitPriceFor(toNumber(group.amount) ?? 0, count);
      return {
        label: group.label || 'Age group',
        count,
        unitPrice,
        total: round2(unitPrice * count),
      };
    });
  } else {
    const unitPrice = unitPriceFor(0, headcount);
    lines = [
      {
        label: 'participant',
        count: headcount,
        unitPrice,
        total: round2(unitPrice * headcount),
      },
    ];
  }

  const total = round2(lines.reduce((sum, line) => sum + line.total, 0));
  const rate = toNumber(taxPercentage) ?? 0;
  const vat = rate > 0 ? round2(total - total / (1 + rate / 100)) : 0;

  return {
    lines,
    total,
    totalBeforeSmartPricing: round2(totalBeforeSmartPricing),
    vat,
    headcount,
    tier,
    tierAmount,
  };
};

export {
  toNumber,
  groupSizeLabel,
  findGroupSizeTier,
  groupSizeCoverageError,
  calculatePricingPreview,
};
export type { IAgeGroupRow, IGroupSizeRow, IDurationRow, IPreviewLine };
